// NO-RAW-LINKS-IN-CHAT (Marin, 13.07) — rendering-side rule.
//
// When a chat message renders a TransferPitch card for a platform link,
// the raw URL is never shown as text. This module is the ONE reusable
// decision, mounted per chat surface (current inbox today; the coming
// thread view reuses it unchanged). It is rendering-only: sending,
// storage, and the card components are untouched, so OLD messages become
// clean automatically.
//
// Two independent card sources exist in chat today:
//
//   (a) TEXT-DERIVED profile cards — ProfileCardPreviewRenderer parses
//       /search/player|coach/:id URLs out of message.text and renders a
//       ProfileCard for each. ProfileCard self-heals: on notfound/error
//       it re-renders the original URL as a clickable link, so hiding the
//       text URL can never leave the user with nothing (see ProfileCard.js).
//
//   (b) SELF-CONTAINED card attachments — the GetStream custom-attachment
//       types dispatched by CustomAttachment.js (business card, scout
//       request/recommendation, last-minute listing, friendly request,
//       radar player/shortlist share). These render from attachment
//       metadata, independent of the text, so any platform URL in the
//       text is redundant next to them.
//
// KEY SAFETY INVARIANT: a platform URL is hidden ONLY when the message
// actually renders a card for that message. If NO card source is present
// (no text-derived profile match AND no known card attachment) the text
// is returned UNCHANGED — a message can never be emptied by stripping
// alone. External (non-platform) links are never touched.

import { detectProfileLinks } from "../../utils/profileLinkDetector";
import { detectClubLinks } from "../../utils/clubLinkDetector";

// The custom-attachment types that CustomAttachment.js dispatches to a
// real card component. Kept in sync with that file — any type here is a
// self-contained card, so its message text needs no accompanying URL.
export const KNOWN_CARD_ATTACHMENT_TYPES = new Set([
  "business-card",
  "business_card",
  "scout_request_share",
  "scout_recommendation_forward",
  "last_minute_listing",
  "friendly_request",
  "radar_player_share",
  "radar_shortlist_share",
]);

// Platform hosts that serve card-backed pages. Covers production
// (transferpitch.com + www/app/staging/dashboard subdomains), the
// techorigins.io staging/api family, and local dev (localhost:any-port).
const PLATFORM_HOST_RE =
  /^https?:\/\/(?:(?:[a-z0-9-]+\.)*(?:transferpitch\.com|techorigins\.io)|localhost(?::\d+)?)(?=[/:?#]|$)/i;

// Route segments whose pages unfurl as a card. Match is intentionally
// conservative: an unlisted route just means the URL stays visible (a
// safe default), never an empty bubble. `search/<role>` covers the
// profile family; `scout-requests` covers the request share.
const CARD_ROUTE_RE =
  /\/(?:search\/(?:player|coach|staff|agent|agency|club)|scout-requests)\/[\w-]+/i;

// Absolute-URL token matcher used to walk arbitrary text.
const ABS_URL_TOKEN_RE = /https?:\/\/[^\s<>()]+/gi;
// Relative card-route token matcher (users paste address-bar paths too).
const REL_CARD_TOKEN_RE =
  /(?<![\w/])\/(?:search\/(?:player|coach|staff|agent|agency|club)|scout-requests)\/[\w-]+(?![\w/])/gi;

/**
 * Is this URL a TransferPitch link that renders as a card?
 * Absolute: host is a platform host AND path is a known card route.
 * Relative: path is a known card route (host is implicitly ours).
 * Returns false for null/undefined, plain text, and every external URL.
 *
 * @param {string} url
 * @returns {boolean}
 */
export function isPlatformCardLink(url) {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return PLATFORM_HOST_RE.test(trimmed) && CARD_ROUTE_RE.test(trimmed);
  }
  // Relative path form: /search/player/123, /scout-requests/9, …
  if (trimmed.startsWith("/")) {
    return new RegExp(`^${CARD_ROUTE_RE.source}$`, "i").test(trimmed.split(/[?#]/)[0]);
  }
  return false;
}

/**
 * Does the message carry a self-contained card attachment (source (b))?
 * @param {{attachments?: Array<{type?: string}>}} message
 * @returns {boolean}
 */
export function hasCardAttachment(message) {
  const atts = message?.attachments;
  if (!Array.isArray(atts)) return false;
  return atts.some((a) => a && KNOWN_CARD_ATTACHMENT_TYPES.has(a.type));
}

// Collapse whitespace left behind after removing a URL so the bubble
// doesn't show orphaned spaces / blank lines.
function collapse(text) {
  return text
    .replace(/[ \t]{2,}/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^\s+|\s+$/g, "");
}

/**
 * The text-transform helper: return the text that should be DISPLAYED for
 * this message, with card-backed platform URLs hidden. Returns "" when the
 * whole text was just the card's URL (caller then renders no text line and
 * lets the card stand alone). Never returns "" unless a card source is
 * present to carry the content.
 *
 * Card-matching rule (documented):
 *   - No card source at all  → text returned UNCHANGED (link stays visible).
 *   - Text-derived profile card(s) present → strip exactly the matched
 *     /search/player|coach/:id URLs (ProfileCard re-shows them on failure).
 *   - Known card attachment present → additionally strip every platform
 *     card-route URL (absolute or relative) from the text; the attachment
 *     card renders regardless, so this can't produce an empty message.
 *   - External URLs and plain text are always preserved.
 *
 * @param {{text?: string, attachments?: Array}} message
 * @returns {string}
 */
export function getDisplayText(message) {
  const text = message?.text || "";
  if (!text) return text;

  const profileMatches = detectProfileLinks(text);
  const clubMatches = detectClubLinks(text);
  const cardAttachment = hasCardAttachment(message);

  // No card renders for this message → never strip (safety invariant).
  if (
    profileMatches.length === 0 &&
    clubMatches.length === 0 &&
    !cardAttachment
  )
    return text;

  let out = text;

  // (a) Remove text-derived card URLs (player/coach profiles AND club/agency
  //     links — #18). Exact-substring removal so we only touch what a card
  //     renderer matched; both cards self-heal to a link on failure, so this
  //     can never leave an empty bubble.
  for (const m of profileMatches) {
    out = out.split(m.fullUrl).join("");
  }
  for (const m of clubMatches) {
    out = out.split(m.fullUrl).join("");
  }

  // (b) A self-contained card attachment carries the content — hide any
  //     remaining platform card-route URL in the text (absolute or relative).
  if (cardAttachment) {
    out = out.replace(ABS_URL_TOKEN_RE, (u) => (isPlatformCardLink(u) ? "" : u));
    out = out.replace(REL_CARD_TOKEN_RE, "");
  }

  return collapse(out);
}

// ── Inbox-row preview classification (share-aware preview line) ───────────────
//
// The inbox row shows one preview line under the name. When the last message is
// a shared card or a bare platform link, that line must read as a human summary
// ("You shared a player", "Sofia shared a club") — NEVER a raw platform URL.
// This classifier is the shared decision behind that line; the row component
// (CustomChannelPreview) owns only the wording and the "You / <name>" prefix, so
// detection stays here next to the chat renderer it mirrors. External (non-
// platform) links and plain text are never our concern — they fall through
// untouched (returns null → caller shows the text verbatim).

// A shared card attachment → the semantic kind the preview names. Mirrors the
// CustomAttachment dispatch (business / scout / last-minute / friendly / radar)
// plus the generic `share` envelope (share_type tender/club/agent/agency).
// Returns null for stream-native and unknown types.
export function shareKindForAttachment(attachment) {
  switch (attachment?.type) {
    case "radar_player_share":
    case "player_share":
    case "scout_recommendation_forward":
      return "player";
    case "radar_shortlist_share":
      return "shortlist";
    case "last_minute_listing":
    case "scout_request_share":
    case "friendly_request":
      return "listing";
    case "business-card":
    case "business_card":
      return "business_card";
    case "share": {
      const shareType = String(attachment?.share_type || "").toLowerCase();
      if (shareType === "club") return "club";
      if (shareType === "tender") return "listing";
      return "profile"; // agent / agency / unknown share envelope
    }
    default:
      return null;
  }
}

/**
 * Classify the last message for the inbox-row preview line. Reuses the exact
 * detectors the chat renderer uses (detectProfileLinks / detectClubLinks /
 * isPlatformCardLink), so "what counts as a shared card" is one decision.
 *
 * Returns:
 *   - null                   → not a share/link; caller shows plain text (and
 *                              external URLs) verbatim.
 *   - { kind: "text", text } → the message has its own words next to a platform
 *                              link; that note (URL stripped) is the preview —
 *                              never a raw URL, never a canned phrase. Wins even
 *                              next to a card attachment.
 *   - { kind: <semantic> }   → the message is JUST a shared card / bare platform
 *                              link; caller renders the phrase. kind ∈ player |
 *                              club | shortlist | listing | business_card |
 *                              profile.
 *
 * @param {{text?: string, attachments?: Array}} message
 * @returns {null | {kind: string, text?: string}}
 */
export function classifySharePreview(message) {
  const attachments = Array.isArray(message?.attachments)
    ? message.attachments
    : [];
  const text = message?.text || "";

  const profileMatches = detectProfileLinks(text); // player | coach
  const clubMatches = detectClubLinks(text); // club | agency

  // Residual = the message's own words with every recognised platform link
  // removed (typed profile/club links first, then any other platform card-route
  // token — /search/agent, /scout-requests, …). Same removal the chat renderer
  // does, but applied unconditionally: the preview always has a phrase fallback,
  // so it may strip more aggressively than getDisplayText (which must never
  // empty a bubble).
  let residual = text;
  for (const m of profileMatches) residual = residual.split(m.fullUrl).join("");
  for (const m of clubMatches) residual = residual.split(m.fullUrl).join("");
  const beforeGeneric = residual;
  residual = residual.replace(ABS_URL_TOKEN_RE, (u) =>
    isPlatformCardLink(u) ? "" : u
  );
  residual = residual.replace(REL_CARD_TOKEN_RE, "");
  const strippedGenericLink = residual !== beforeGeneric;
  residual = collapse(residual);

  const cardAttachment = attachments.find((a) => shareKindForAttachment(a));
  const hasPlatformLink =
    profileMatches.length > 0 || clubMatches.length > 0 || strippedGenericLink;

  // Neither a shared card nor a platform link → not ours; show text verbatim.
  if (!cardAttachment && !hasPlatformLink) return null;

  // Own words survive the strip → the note is the preview (URL already gone).
  // This holds even next to a card attachment: an accompanying message wins
  // over the canned phrase.
  if (residual) return { kind: "text", text: residual };

  // Nothing but the share left → name the kind. A card attachment is the most
  // precise source; otherwise the link type decides.
  if (cardAttachment) return { kind: shareKindForAttachment(cardAttachment) };
  if (profileMatches.some((m) => m.type === "player")) return { kind: "player" };
  if (profileMatches.length > 0) return { kind: "profile" }; // coach-only
  if (clubMatches.some((m) => m.type === "club")) return { kind: "club" };
  if (clubMatches.length > 0) return { kind: "profile" }; // agency-only
  return { kind: "profile" }; // bare platform card link (agent/staff/scout-request)
}
